// Membership manager — Kingdom Club tier. Membership grants:
//   • +10% Mao Coin per paw click (applied in seasonManager via getClickBonusMult)
//   • Full Registry deep-view of other players (vs. stripped public summary)
//
// SERVER is authoritative: the real source of truth is the account record,
// flipped by the Stripe webhook on `checkout.session.completed` (→ KINGDOM_CLUB)
// and `customer.subscription.deleted` / `invoice.payment_failed` (→ FREE).
// getMembership() reads accountManager's cached account tier first, falling
// back to the locally-persisted profile value only when offline.

import { Events, emit, on } from '../ui/eventBus.js'
import { MembershipConfig } from '../config/balance.js'
import { getProfile, persist } from './state.js'
import { getAccountInfo, startCheckout, devActivateMembership } from './accountManager.js'

export function init() {
  on('membership:subscribe', () => openCheckout())
  on('membership:redeem', ({ code }) => redeemCode(code))
  // Keep the local profile mirror in sync when the server flips our tier
  // (so offline reads + the Registry's own profile.membership stay correct).
  on(Events.MembershipChange, ({ tier }) => {
    const p = getProfile()
    if (p.membership !== tier) {
      p.membership = tier
      if (tier === 'KINGDOM_CLUB' && !p.membershipSince) p.membershipSince = Date.now()
      if (tier === 'FREE') p.membershipSince = 0
      persist()
    }
  })
}

export function getMembership() {
  // Account tier (server truth) wins; profile is the offline fallback.
  const acct = getAccountInfo()
  if (acct?.online) return acct.membership ?? 'FREE'
  return getProfile().membership ?? 'FREE'
}

export function getTier() {
  return MembershipConfig.tiers[getMembership()] ?? MembershipConfig.tiers.FREE
}

export function isMember() {
  return getMembership() === 'KINGDOM_CLUB'
}

export function getClickBonusMult() {
  return getTier().clickBonusMult ?? 1
}

export function canSeeFullRegistry() {
  return getTier().registryAccess === 'full'
}

// Open Stripe Checkout. Asks the server for a session URL (real Stripe or a
// payment link with our userId embedded) and opens it in a new tab. The
// webhook flips membership server-side once payment lands; the client picks
// it up via accountManager.refreshMembership() on return. If billing isn't
// configured, falls back to the dev activation path.
export async function openCheckout() {
  const res = await startCheckout()
  if (!res.ok && res.notConfigured) {
    // No Stripe configured (local dev) → flip via the dev shortcut.
    return devActivateMembership('KINGDOM_CLUB')
  }
  return res
}

// Redeem-code path — kept for dev/QA. Routes through the server dev-activate
// so the authoritative account record flips (not just the local profile).
export async function redeemCode(code) {
  if (code === MembershipConfig.devRedeemCode) {
    const res = await devActivateMembership('KINGDOM_CLUB')
    return !!res.ok
  }
  return false
}

// Idempotent setter. Call this from the Stripe webhook handler (or the
// dev redeem flow) to flip tiers.
export function applyMembership(tier) {
  const p = getProfile()
  if (!MembershipConfig.tiers[tier]) return false
  if (p.membership === tier) return false
  p.membership = tier
  if (tier === 'KINGDOM_CLUB') p.membershipSince = Date.now()
  persist()
  emit(Events.MembershipChange, { tier })
  return true
}
